import { useCreateTicket } from '@services/tickets/mutations/useCreateTicket'
import type { TicketCategory, TicketPriority } from '@services/tickets/types'
import { Wrench } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

import { TICKET_CATEGORIES, TICKET_PRIORITIES } from '../constants'
import { AttachmentPicker } from './AttachmentPicker'

export function TicketCreateDialog() {
  const { t } = useTranslation('tickets')
  const createTicket = useCreateTicket()
  const [open, setOpen] = useState(false)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<TicketCategory | undefined>()
  const [priority, setPriority] = useState<TicketPriority>('NORMAL')
  const [files, setFiles] = useState<File[]>([])

  const canSubmit = title.trim().length > 2 && !!category && !createTicket.isPending

  const reset = () => {
    setTitle('')
    setDescription('')
    setCategory(undefined)
    setPriority('NORMAL')
    setFiles([])
  }

  const submit = () => {
    if (!canSubmit || !category) return
    createTicket.mutate(
      {
        title: title.trim(),
        description: description.trim(),
        category,
        priority,
        attachments: files
      },
      {
        onSuccess: () => {
          setOpen(false)
          reset()
        }
      }
    )
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Wrench className="h-4 w-4" />
        {t('create.button')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Wrench className="h-5 w-5" />
              </span>
              <div>
                <DialogTitle>{t('create.title')}</DialogTitle>
                <p className="m-0 mt-1 text-sm text-muted-foreground">
                  {t('create.subtitle')}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="mb-1.5 block">{t('form.category')} *</Label>
              <Combobox
                searchable={false}
                value={category}
                onChange={(v) => setCategory(v as TicketCategory)}
                placeholder={t('form.category_placeholder')}
                options={TICKET_CATEGORIES.map((value) => ({
                  value,
                  label: t(`categories.${value}`)
                }))}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('form.priority')}</Label>
              <Combobox
                searchable={false}
                value={priority}
                onChange={(v) => setPriority((v as TicketPriority) ?? 'NORMAL')}
                options={TICKET_PRIORITIES.map((value) => ({
                  value,
                  label: t(`priorities.${value}`)
                }))}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1.5 block">{t('form.title')} *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('form.title_placeholder')}
                maxLength={255}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1.5 block">{t('form.description')}</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('form.description_placeholder')}
                rows={4}
              />
            </div>
            <div className="sm:col-span-2">
              <AttachmentPicker files={files} onChange={setFiles} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghostDark" onClick={() => setOpen(false)}>
              {t('form.cancel')}
            </Button>
            <Button onClick={submit} disabled={!canSubmit}>
              {t('create.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
